import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import "katex/dist/katex.min.css";
import LogoIcon from "../img/agent-logo.png";
import "../index.css";
import Latex from "react-latex-next";
import Lottie from "lottie-react";
import loadingSpinner from "../img/loading-spinner1.gif";
import InputButton from "../img/input_btn.png";
import HamburgerButton from "../img/hamburger_btn.png";
import InputActivateButton from "../img/input_btn_activated.png";
import MicButton from "../img/mic_img_bold.png";
import MicActivateButton from "../img/mic_img_bold_white.png";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";

const StudyPlan = ({
  planMessages,
  setPlanMessages,
  nickname,
  setNickname,
  isAuthenticated,
  setIsAuthenticated,
  ws,
  handleLogout,
}) => {
  const navigate = useNavigate();
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showStartMenu, setShowStartMenu] = useState(planMessages.length === 1);
  const messagesEndRef = useRef(null);
  const [showIntro, setShowIntro] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentTypingMessageId, setCurrentTypingMessageId] = useState(null);
  const [volume, setVolume] = useState(1.0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("고등수학 에이전트");
  const [currentView, setCurrentView] = useState("studyPlan");
  const [password, setPassword] = useState("");
  const [isQuestionPhase, setIsQuestionPhase] = useState(() => {
    const saved = localStorage.getItem("studyPlanState");
    if (saved) {
      const state = JSON.parse(saved);
      return state.isQuestionPhase;
    }
    return true;
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const saved = localStorage.getItem("studyPlanState");
    if (saved) {
      const state = JSON.parse(saved);
      return state.currentQuestionIndex;
    }
    return -1;
  });
  const [loadingDots, setLoadingDots] = useState(".");
  const [agentStatus, setAgentStatus] = useState("");

  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  recognition.lang = "ko-KR";
  recognition.continuous = false;
  recognition.interimResults = false;

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [planMessages]);

  useEffect(() => {
    if (planMessages.length === 0) {
      setPlanMessages([
        {
          id: Date.now(),
          text: `${nickname} 학생이 고등수학을 끝까지 완독할 수 있도록 학습 플랜을 세워드릴게요! 📚✨ 계획하고 있는 공부 기간을 말씀해주세요! 💭`,
          isUser: false,
          options: ["1주일", "1개월", "3개월", "6개월"],
        },
      ]);
    }
  }, [nickname]);

  useEffect(() => {
    let count = 0;
    let intervalId;

    if (isLoading) {
      intervalId = setInterval(() => {
        count = (count + 1) % 4;
        setLoadingDots(".".repeat(count));
      }, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    localStorage.setItem(
      "studyPlanState",
      JSON.stringify({
        isQuestionPhase,
        currentQuestionIndex,
      })
    );
  }, [isQuestionPhase, currentQuestionIndex]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 22 || hour < 6) {
      return `조용히 집중하기 좋은 시간이에요. 서책 에이전트가 함께 할게요.🌙`;
    } else if (hour < 12) {
      return `공부하기 좋은 아침이네요. 오늘 하루도 좋은 하루가 될 거예요.☀️`;
    } else if (hour < 18) {
      return `서책 에이전트와 오후 시간도 알차게 보내봐요.💪`;
    } else {
      return `저녁 공부도 힘차게 해볼까요? 서책 에이전트가 끝까지 응원할게요.😃`;
    }
  };

  const showGreetingWithTypingEffect = async () => {
    const messageId = Date.now();
    setPlanMessages([
      {
        id: messageId,
        text: "",
        isUser: false,
      },
    ]);

    const greeting = getGreeting();
    const fullText = `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">${nickname} 학생, ${greeting}</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">원하는 메뉴를 선택해 주세요!</h3>`;
    let currentText = "";

    for (let i = 0; i < fullText.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 20));
      currentText += fullText[i];
      setPlanMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, text: currentText } : msg
        )
      );
    }
    return new Promise((resolve) => setTimeout(resolve, 500));
  };

  const getNextQuestionWithReaction = (questionIndex, response) => {
    let reaction = "";
    const styleStart = `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">`;
    const styleEnd = `</h3>`;

    switch (questionIndex) {
      case -1: // 공부 기간 선택 후
        const periodMap = {
          "1주일":
            "1주일이라는 짧은 기간 동안 집중적으로 공부하실 계획이시군요!",
          "1개월": "1개월 동안 차근차근 공부하실 계획이시군요!",
          "3개월": "3개월이면 충분히 깊이있게 공부하실 수 있을 거예요!",
          "6개월": "6개월 동안 꾸준히 공부하실 계획이시군요. 멋져요!",
        };
        reaction = periodMap[response] || "좋은 계획이에요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}주중과 주말 중 어떤 날에 공부하시나요? 📅${styleEnd}`;

      case 0: // 공부 요일 선택 후
        const dayMap = {
          "주중(월~금)": "평일에 집중해서 공부하시는 게 좋겠네요!",
          "주말(토~일)": "주말을 알차게 보내실 계획이시군요!",
          "주중과 주말 모두": "매일 꾸준히 공부하시려는 모습이 멋져요!",
        };
        reaction = dayMap[response] || "좋은 선택이에요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}하루에 공부할 수 있는 시간이 얼마나 될까요? ⏰${styleEnd}`;

      case 1: // 공부 시간 선택 후
        const timeMap = {
          "30분": "짧더라도 매일 꾸준히 하는 게 중요해요!",
          "1시간": "1시간이면 충분히 효율적으로 공부할 수 있어요!",
          "2시간": "2시간 동안 집중해서 공부하시면 많은 성과가 있을 거예요!",
          "3시간 이상": "정말 열정적이시군요! 중간중간 꼭 쉬어가면서 해요!",
        };
        reaction = timeMap[response] || "시간 관리를 잘 하실 수 있을 거예요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}한 번에 몇 문제 정도 풀 수 있을 것 같나요? 📝${styleEnd}`;

      case 2: // 문제 개수 선택 후
        const problemMap = {
          "5문제": "한 문제 한 문제 꼼꼼하게 풀어보는 거죠!",
          "10문제": "적당한 양이에요! 차근차근 해결해봐요!",
          "20문제": "많은 문제를 통해 다양한 유형을 접할 수 있을 거예요!",
          "30문제 이상": "대단한 열정이네요! 화이팅!",
        };
        reaction = problemMap[response] || "좋은 계획이에요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}선호하는 학습 방식이 있나요? 💡${styleEnd}`;

      case 3: // 학습 방식 선택 후
        const methodMap = {
          "균등 분배 (매일 일정량 풀기)": "꾸준함이 성공의 열쇠죠!",
          "개념별 집중 학습":
            "개념별로 깊이 있게 공부하면 확실한 이해가 가능할 거예요!",
          "난이도 조절(쉬운 문제 → 어려운 문제)":
            "단계별로 학습하면 실력이 쑥쑥 늘 거예요!",
          "모의고사 방식":
            "실전처럼 공부하면서 시간 관리 능력도 키울 수 있겠네요!",
          "반복 학습(2회독, 3회독)":
            "반복은 학습의 어머니죠! 꾸준한 반복으로 실력을 쌓아가봐요!",
        };
        reaction = methodMap[response] || "좋은 학습 방식이네요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}거의 다 왔어요! 오답 정리를 어떻게 하고 싶나요? 🪄${styleEnd}`;

      case 4: // 오답 정리 방식 선택 후
        const reviewMap = {
          "틀린 문제만 다시 풀기":
            "틀린 문제를 다시 풀어보면서 실수를 줄여나갈 수 있을 거예요!",
          "오답 노트 만들기": "오답 노트로 체계적인 복습이 가능할 거예요!",
          "해설 보고 개념 정리":
            "개념을 다시 한 번 정리하면서 더 깊이 이해할 수 있겠네요!",
        };
        reaction = reviewMap[response] || "좋은 방법이에요!";
        return `${styleStart}${reaction}${styleEnd}\n\n${styleStart}마지막 질문이에요! 주간 복습을 포함할까요? 📑${styleEnd}`;

      case 5: // 마지막 질문 (주간 복습) 후
        const weeklyReviewMap = {
          "네 (매주 복습일 포함)":
            "주기적인 복습으로 더 탄탄하게 실력을 쌓을 수 있을 거예요! ✨",
          "아니오(연속적으로 진행)": "쉬지 않고 달려나가는 열정이 대단해요! ✨",
        };
        reaction = weeklyReviewMap[response] || "알겠습니다! ✨";
        return `${styleStart}${reaction}\n\n지금까지 말씀해주신 내용을 바탕으로 맞춤 학습 플랜을 만들어드릴게요.${styleEnd}`;

      default:
        return "";
    }
  };

  const handleUserResponse = (response) => {
    const currentTime = Date.now();

    if (currentQuestionIndex === -1) {
      setCurrentQuestionIndex(0);
      setPlanMessages([
        {
          id: currentTime - 1,
          text: `${nickname} 학생이 고등수학을 끝까지 완독할 수 있도록 학습 플랜을 세워드릴게요! 📚✨ 계획하고 있는 공부 기간을 말씀해주세요! 💭`,
          isUser: false,
          options: ["1주일", "1개월", "3개월", "6개월"],
          optionSelected: true,
        },
        {
          id: currentTime - 0.5,
          text: response,
          isUser: true,
        },
        {
          id: currentTime,
          text: getNextQuestionWithReaction(-1, response),
          isUser: false,
          options: questions[0].options,
        },
      ]);
    } else if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setPlanMessages((prev) => {
        const updatedMessages = prev.map((msg) => {
          if (msg.options && !msg.optionSelected) {
            return {
              ...msg,
              optionSelected: true,
            };
          }
          return msg;
        });
        return [
          ...updatedMessages,
          {
            id: currentTime - 0.5,
            text: response,
            isUser: true,
          },
          {
            id: currentTime,
            text: getNextQuestionWithReaction(currentQuestionIndex, response),
            isUser: false,
            options: questions[currentQuestionIndex + 1].options,
          },
        ];
      });
    } else {
      // 마지막 질문에 대한 응답 처리
      setPlanMessages((prev) => {
        const updatedMessages = prev.map((msg) => {
          if (msg.options && !msg.optionSelected) {
            return {
              ...msg,
              optionSelected: true,
            };
          }
          return msg;
        });
        return [
          ...updatedMessages,
          {
            id: currentTime - 0.5,
            text: response,
            isUser: true,
          },
          {
            id: currentTime,
            text: getNextQuestionWithReaction(currentQuestionIndex, response),
            isUser: false,
          },
        ];
      });
      setIsQuestionPhase(false);
      setTimeout(() => {
        generateStudyPlan();
      }, 1500); // 리액션 메시지를 읽을 시간을 준 후 학습 플랜 생성
    }
  };

  const handleOptionSelect = (selectedOption) => {
    // 현재 메시지의 옵션들을 숨기고 선택된 옵션만 표시
    setPlanMessages((prev) =>
      prev.map((msg) => {
        if (msg.options && !msg.optionSelected) {
          return {
            ...msg,
            optionSelected: true,
          };
        }
        return msg;
      })
    );

    // 다음 질문 처리 - 사용자 메시지는 여기서만 추가됨
    handleUserResponse(selectedOption);
  };

  const resetPlan = () => {
    // 모든 상태를 초기 상태로 리셋
    setIsQuestionPhase(true);
    setCurrentQuestionIndex(-1);
    setInputMessage("");

    // 초기 메시지로 완전 초기화
    const initialMessage = {
      id: Date.now(),
      text: `${nickname} 학생이 고등수학을 끝까지 완독할 수 있도록 학습 플랜을 세워드릴게요! 📚✨ 계획하고 있는 공부 기간을 말씀해주세요! 💭`,
      isUser: false,
      options: ["1주일", "1개월", "3개월", "6개월"],
      optionSelected: false,
    };

    // planMessages를 초기 상태로 완전히 리셋
    setPlanMessages([initialMessage]);

    // localStorage에서 모든 관련 상태 초기화
    localStorage.setItem("planMessages", JSON.stringify([initialMessage]));
    localStorage.setItem(
      "studyPlanState",
      JSON.stringify({
        isQuestionPhase: true,
        currentQuestionIndex: -1,
      })
    );

    setShowStartMenu(true);

    // 스크롤을 최상단으로 이동
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleLogin = () => {
    if (password === "123456" && nickname.trim() !== "") {
      setIsAuthenticated(true);
      setShowIntro(true);
      setTimeout(() => {
        setShowIntro(false);
      }, 2000);
    } else if (nickname.trim() === "") {
      alert("닉네임을 입력해주세요.");
    } else {
      alert("비밀번호가 틀렸습니다.");
    }
  };

  const handleTitleClick = () => {
    if (isAuthenticated) {
      setShowStartMenu(true);
    }
  };

  const MessageContent = ({ text, messageId, imageUrl }) => {
    const processLatex = (text) => {
      return text
        .replace(/\\\[(.*?)\\\]/g, (_, match) => {
          return `$$ {\\displaystyle ${match}} $$`;
        })
        .replace(/\\\((.*?)\\\)/g, (_, match) => `$ ${match} $`);
    };

    const textStyle = {
      fontFamily: "Pretendard",
      fontSize: "14px",
      //   fontWeight: "700",
      lineHeight: "28.8px",
      letterSpacing: "-0.001em",
      textAlign: "left",
      textUnderlinePosition: "from-font",
      textDecorationSkipInk: "none",
    };

    return (
      <div
        style={{
          width: "100%",
          "& .katex-display": {
            overflow: "hidden",
            maxWidth: "100%",
          },
          "& .katex": {
            maxWidth: "100%",
            display: "block",
          },
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Generated content"
            className="max-w-full h-auto rounded-lg mb-2"
            style={{ maxHeight: "200px", objectFit: "contain" }}
          />
        )}

        {text.includes("<h3") ? (
          <div dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <div style={textStyle}>
            <Latex>{processLatex(text.split("\n")[0])}</Latex>
            {text.split("\n").slice(1).join("\n") && (
              <Latex>
                {processLatex(text.split("\n").slice(1).join("\n"))}
              </Latex>
            )}
          </div>
        )}
      </div>
    );
  };

  const startListening = () => {
    const recognition = new (window.SpeechRecognition ||
      window.webkitSpeechRecognition)();
    recognition.lang = "ko-KR";
    recognition.interimResults = true;
    recognition.continuous = true;

    let silenceTimer;
    const SILENCE_DURATION = 1800;

    recognition.onresult = (event) => {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        recognition.stop();
      }, SILENCE_DURATION);

      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join("");

      setInputMessage(transcript);
    };

    recognition.onstart = () => {
      setIsListening(true);
      silenceTimer = setTimeout(() => {
        recognition.stop();
      }, SILENCE_DURATION);
    };

    recognition.onend = () => {
      setIsListening(false);
      clearTimeout(silenceTimer);
    };

    recognition.start();
  };

  recognition.onerror = (event) => {
    console.error("음성 인식 오류:", event.error);
    setIsListening(false);
  };

  const renderMessage = (message) => {
    return (
      <>
        <div
          key={message.id}
          className={`flex ${
            message.isUser ? "justify-end" : "justify-start"
          } mb-4`}
        >
          {!message.isUser && !message.isLoading && (
            <div className="flex animate-fadeIn">
              <img
                src={LogoIcon}
                alt="Bot Profile"
                className="w-8 h-8 rounded-full mr-2"
              />
              <div className="max-w-[90%] rounded-lg p-1 break-words bg-white text-gray-800">
                <MessageContent
                  text={message.text}
                  imageUrl={message.imageUrl}
                />
              </div>
            </div>
          )}
          {!message.isUser && message.isLoading && (
            <div className="max-w-[90%] rounded-lg p-1 break-words bg-white text-gray-800 ml-10">
              {/* <MessageContent
                text={`학습 계획을 생성하고 있어요${loadingDots}`}
                imageUrl={message.imageUrl}
              /> */}
            </div>
          )}
          {message.isUser && (
            <div className="shadow-custom max-w-[80%] rounded-[100px] pt-[8px] pr-[16px] pb-[8px] pl-[16px] break-words bg-customPurple text-white font-bold tracking-[0.015em]">
              <MessageContent text={message.text} />
            </div>
          )}
        </div>
        {!message.isUser && message.options && !message.optionSelected && (
          <div className="flex flex-col items-end mb-4">
            <div className="flex flex-wrap gap-[8px] justify-end max-w-[80%]">
              {message.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(option)}
                  className="flex items-center justify-center whitespace-nowrap shadow-custom pt-[8px] pr-[16px] pb-[8px] pl-[16px] bg-white border-2 border-IntroButtonBorderColor rounded-[100px] hover:bg-gray-50 transition duration-1000 ease-in-out font-normal text-AIMessageTextColor"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const changeAgent = (newAgent) => {
    Swal.fire({
      title: "🤖",
      text: "현재는 고등수학 에이전트만 사용 가능합니다",
      icon: "info",
      confirmButtonText: "알겠어요!",
      confirmButtonColor: "#7950F2",
      background: "#fff",
      width: "280px",
      padding: "10px",
      customClass: {
        popup: "rounded-2xl",
        title: "text-xl mb-2",
        content: "text-xs",
        confirmButton: "rounded-full px-4 py-1.5 text-xs font-medium",
      },
    });
  };

  const agentMenuItems = ["고등영어 에이전트", "고등국어 에이전트"];

  const handleBack = () => {
    navigate("/chat");
  };

  const generateStudyPlan = async () => {
    setIsLoading(true);
    try {
      const userResponses = planMessages
        .filter((m) => m.isUser)
        .map((m) => m.text)
        .join("\n");

      const messageData = {
        messages: [
          {
            role: "system",
            content: "당신은 학습 계획을 수립하는 전문가입니다.",
          },
          {
            role: "user",
            content: `다음은 사용자의 학습 관련 답변들입니다:\n${userResponses}\n\n이 정보를 바탕으로 구체적인 학습 계획을 수립해주세요.`,
          },
        ],
      };

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(messageData));
      } else {
        throw new Error("WebSocket is not connected");
      }
    } catch (error) {
      console.error("학습 계획 생성 오류:", error);
      setPlanMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: "학습 계획 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
          isUser: false,
        },
      ]);
      setIsLoading(false);
    }
  };

  const formatMessagesForGPT = (messages) => {
    return messages.map((msg) => ({
      role: msg.isUser ? "user" : "assistant",
      content: msg.text,
    }));
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "" || isLoading || !ws) return;

    if (isQuestionPhase) {
      handleUserResponse(inputMessage);
      setInputMessage("");
    } else {
      const userMessage = {
        id: Date.now(),
        text: inputMessage,
        isUser: true,
      };

      setPlanMessages((prev) => [...prev, userMessage]);
      setInputMessage("");
      setIsLoading(true);

      try {
        const messageData = {
          messages: formatMessagesForGPT([...planMessages, userMessage]),
        };

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(messageData));
        } else {
          throw new Error("WebSocket is not connected");
        }
      } catch (error) {
        console.error("메시지 전송 오류:", error);
        setPlanMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            text: "죄송합니다. 오류가 발생했습니다. 다시 시도해주세요.",
            isUser: false,
          },
        ]);
        setIsLoading(false);
      }
    }
  };

  const questions = [
    {
      options: ["주중(월~금)", "주말(토~일)", "주중과 주말 모두"],
    },
    {
      options: ["30분", "1시간", "2시간", "3시간 이상"],
    },
    {
      options: ["5문제", "10문제", "20문제", "30문제 이상"],
    },
    {
      options: [
        "균등 분배 (매일 일정량 풀기)",
        "개념별 집중 학습",
        "난이도 조절(쉬운 문제 → 어려운 문제)",
        "모의고사 방식",
        "반복 학습(2회독, 3회독)",
      ],
    },
    {
      options: [
        "틀린 문제만 다시 풀기",
        "오답 노트 만들기",
        "해설 보고 개념 정리",
      ],
    },
    {
      options: ["네 (매주 복습일 포함)", "아니오(연속적으로 진행)"],
    },
  ];

  // 에이전트 타입에 따른 로딩 메시지 반환
  const getLoadingMessage = (agentType) => {
    const messages = {
      default: "질문을 분석하고 있어요...",
      generate_plot: "질문을 분석하고 있어요...",
      study_plan: "질문을 분석하고 있어요...",
      calculate: "질문을 분석하고 있어요...",
      web_search: "질문을 분석하고 있어요...",
    };
    return messages[agentType] || messages.default;
  };

  // ws.onmessage 이벤트 핸들러 추가
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      const response = JSON.parse(event.data);
      console.log("Received message:", response);

      if (response.event === "agent_status") {
        const statusData = JSON.parse(response.data);
        if (!currentTypingMessageId) {
          try {
            const decodedMessage = decodeURIComponent(
              JSON.parse('"' + statusData.message.replace(/\"/g, '\\"') + '"')
            );
            setAgentStatus(decodedMessage);
          } catch (error) {
            console.error("메시지 디코딩 실패:", error);
            setAgentStatus(statusData.message);
          }
        }
      } else if (response.event === "result") {
        setIsLoading(false);
        const resultData = JSON.parse(response.data);
        const aiMessageId = Date.now() + 1;
        setCurrentTypingMessageId(aiMessageId);

        setAgentStatus("");

        let imageUrl = null;
        if (resultData.fileData) {
          try {
            const decodedData = decodeURIComponent(
              escape(window.atob(resultData.fileData))
            );
            imageUrl = `data:${resultData.mediaType};base64,${btoa(
              decodedData
            )}`;
          } catch (error) {
            imageUrl = `data:${resultData.mediaType};base64,${resultData.fileData}`;
          }
        }

        // 타이핑 효과 대신 바로 전체 메시지를 표시
        const aiMessage = {
          id: aiMessageId,
          text: resultData.botMessage,
          isUser: false,
          imageUrl: imageUrl,
        };

        setPlanMessages((prev) => [...prev, aiMessage]);
        setCurrentTypingMessageId(null);
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [ws, currentTypingMessageId]);

  return (
    <div className="fixed inset-0 flex items-center justify-center">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        currentAgent={currentAgent}
        nickname={nickname}
        handleLogout={handleLogout}
      />

      <div className="bg-white p-2 sm:p-4 rounded-lg shadow-md h-[100dvh] w-full max-w-sm sm:max-w-md flex flex-col">
        <div className="flex flex-col h-full">
          <div className="flex flex-row items-center">
            <div className="flex items-center self-start mt-4">
              <img
                src={HamburgerButton}
                alt="메뉴"
                className="w-4 h-4 cursor-pointer"
                onClick={toggleSidebar}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-center mb-0.5 sm:mb-1">
                <h1
                  className="text-center text-lg sm:text-xl font-bold cursor-pointer"
                  onClick={handleTitleClick}
                >
                  Book Agent
                </h1>
              </div>
              <div className="flex justify-center items-center relative">
                <div
                  className="text-center text-xs sm:text-sm mb-2 sm:mb-4 pb-1 sm:pb-2 border-b"
                  style={{ color: "#86878C" }}
                >
                  문제집 또는 수학관련 질문 외에는 답변을 할 수 없습니다.
                </div>
                <button
                  onClick={resetPlan}
                  className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors duration-200 flex items-center gap-1 absolute right-[-30px] top-[0px]"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex items-center self-start mt-2 w-5 h-5"></div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-4 py-2">
              {planMessages.map((message) => renderMessage(message))}
              {isLoading && !currentTypingMessageId && (
                <div className="flex justify-start animate-fadeIn">
                  <img
                    src={LogoIcon}
                    alt="Bot Profile"
                    className="w-8 h-8 rounded-full mr-2"
                  />
                  <div className="flex items-center">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-600 mb-1">
                        {agentStatus || getLoadingMessage(currentAgent)}
                      </span>
                      <img
                        src={loadingSpinner}
                        alt="Loading..."
                        style={{ width: 42, height: 42 }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="border pt-[8px] pr-[12px] pb-[8px] pl-[12px] bg-InputBarColor rounded-[12px]">
            <div className="flex gap-1">
              <input
                type="text"
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder="학습 계획에 대해 물어보세요."
                className="flex-1 rounded-lg focus:outline-none focus:ring-0 bg-InputBarColor"
                disabled={isLoading}
              />
              <button
                onClick={startListening}
                className={`px-1 rounded-full ${
                  isListening ? "bg-red-400 w-8 h-8" : "bg-gray-100 w-10 h-9"
                } flex items-center justify-center transition-all duration-200`}
                disabled={isLoading}
              >
                <img
                  src={isListening ? MicActivateButton : MicButton}
                  alt="음성 인식"
                  className={`transition-transform duration-100 ${
                    isListening ? "w-6 h-6" : "w-8 h-8"
                  } ${isListening ? "animate-pulse" : ""}`}
                  style={{
                    transform: isListening ? `scale(${volume})` : "scale(1)",
                  }}
                />
              </button>
              <button
                onClick={handleSendMessage}
                className={`p-0 text-white rounded-full`}
                disabled={isLoading}
              >
                <img
                  src={inputMessage.trim() ? InputActivateButton : InputButton}
                  alt="전송 버튼"
                  className="w-8 h-8 transition-transform duration-200 ease-in-out transform hover:scale-95 active:scale-90"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlan;
